import React from 'react';
import {useSelector} from 'react-redux';
import {DndContext, DragOverlay} from '@dnd-kit/core';
import * as css from './TimetableBoard.css';
import TimetableHeader from '../timetableHeader/TimetableHeader';
import TimetableGrid from '../timetableGrid/TimetableGrid';
import NoDateLecturesGrid from '../noDateLecturesGrid/NoDateLecturesGrid';
import ActionsBar from '../timetableGrid/actionsBar/ActionsBar';
import LectureCard from '../lectureCard/LectureCard';
import Loader from '../../../../component/loader/Loader';
import {RootState} from '../../../../redux/store.type';
import {TTimetableItem} from '../../../../redux/timetable/timetable.type';
import {useTimetableDays} from '../../hooks/useTimetableDays';
import {useTimeGroups} from '../../hooks/useTimeGroups';
import {useDragLecture} from '../../hooks/useDragLecture';
import {useTimetableSearch} from '../../hooks/useTimetableSearch';

type Props = {
    expanded: boolean;
    days: ReturnType<typeof useTimetableDays>;
    conferenceDatesAction: {label: string; onClick: () => void; icon?: React.ReactNode};
    onAddLecture: (tsid?: number | null) => void;
    onEditLecture: (lecture: TTimetableItem) => void;
    onAddBreak: () => void;
    onBreakEdit: (item: TTimetableItem) => void;
};

function TimetableBoard({expanded, days, conferenceDatesAction, onAddLecture, onEditLecture, onAddBreak, onBreakEdit}: Props) {
    const lecturesByDay = useSelector((state: RootState) => state.timetable.lectures);
    const slots = useSelector((state: RootState) => state.timetable.slots);
    const getLecturesXHR = useSelector((state: RootState) => state.timetable.getLecturesXHR);

    const hasDate = days.currentDayIndex !== -1;
    const lectures = hasDate ? (lecturesByDay[days.currentDay?.timestamp] ?? []) : days.noDateItems;
    const {searchQuery, setSearchQuery, visibleLectures} = useTimetableSearch(lectures);
    const timeGroups = useTimeGroups(visibleLectures, hasDate ? days.currentDay : undefined, slots);
    const drag = useDragLecture(lecturesByDay);
    const isLoading = getLecturesXHR === 'LOADING';
    const isEmpty = !isLoading && hasDate && timeGroups.length === 0;

    const handleMenuClick = (item: TTimetableItem) => {
        if(item.isBreak) onBreakEdit(item);
    };

    return (
        <DndContext sensors={drag.sensors} onDragStart={drag.handleDragStart} onDragEnd={drag.handleDragEnd}>
            <css.board>
                <TimetableHeader conferenceDatesAction={conferenceDatesAction} searchQuery={searchQuery} onSearchChange={setSearchQuery} />
                <css.container $isEmpty={isEmpty}>
                    {isLoading ? <Loader XHR="LOADING" height="100%">{null}</Loader> : (
                        <css.contentArea>
                            {hasDate
                                ? <TimetableGrid timeGroups={timeGroups} expanded={expanded} handlers={{onAddLecture: (tsid: number | null) => onAddLecture(tsid), onAddBreak: hasDate ? onAddBreak : undefined, onEditLecture, onMenuClick: handleMenuClick}} />
                                : <NoDateLecturesGrid lectures={visibleLectures} expanded={expanded} onEditLecture={onEditLecture} />
                            }
                        </css.contentArea>
                    )}
                    {!isLoading && !isEmpty && (
                        <ActionsBar
                            onAddItem={() => onAddLecture()}
                            onAddBreak={onAddBreak}
                            disableBreak={!hasDate}
                        />
                    )}
                </css.container>
            </css.board>
            <DragOverlay>
                {drag.activeLecture && (
                    <css.dragOverlayCard $isActive={!!drag.activeLecture}>
                        <LectureCard lecture={drag.activeLecture} expanded={expanded} isOverlay />
                    </css.dragOverlayCard>
                )}
            </DragOverlay>
        </DndContext>
    );
}

export default TimetableBoard;
